import type { IntakeResponse } from '../../services/api';
import type {
  EpisodeListItem,
  PatientMatchResult,
} from '../../hooks/useCreatePatientForm/types';

/**
 * Value for `exclude_patient_id` on Roger duplicate check: only when this intake *created*
 * that patient; omit when the intake matched an existing patient (so they stay in results).
 */
export function excludePatientIdForRogerDuplicateCheck(
  intake:
    | Pick<IntakeResponse, 'is_new_patient' | 'patient_id'>
    | null
    | undefined
): string | null {
  if (!intake) return null;
  return intake.is_new_patient === true ? (intake.patient_id ?? null) : null;
}

/**
 * Pick the latest episode (by start date, most recent first) that a ROC could resume —
 * i.e. the most recent episode that is still active (not ROC-ignorable). An inactive
 * episode never represents the patient's current episode, so it is skipped, even if its
 * admission is still open. Returns null when no active episode exists — e.g. when every
 * episode is inactive, in which case there are no matched-episode dates to surface or to
 * constrain a SOC. Used when saving match data and when scheduling in Kinnser.
 */
export function getLatestActiveEpisode(
  episodes: EpisodeListItem[]
): EpisodeListItem | null {
  if (!episodes.length) return null;
  const sorted = [...episodes].sort((a, b) => {
    const dateA = new Date(a.start_date).getTime();
    const dateB = new Date(b.start_date).getTime();
    return dateB - dateA;
  });
  const filtered = sorted.filter(e => !isIgnorableEpisodeForRoc(e));
  return filtered.length > 0 ? filtered[0] : null;
}

/** True when the episode's admission carries a non-empty discharge date. */
function hasAdmissionDischargeDate(ep: EpisodeListItem): boolean {
  return Boolean(ep.discharge_date && ep.discharge_date.trim() !== '');
}

/**
 * SOC view of "ignore this episode". When reasoning about whether a *new admission*
 * (SOC) can be created, we ignore an episode whose admission is inactive, or that has
 * been discharged and is itself inactive. Such episodes can't block a new admission and
 * never represent the patient's open admission.
 *
 * Note this is deliberately different from the ROC view (`isIgnorableEpisodeForRoc`): an
 * open admission sitting on an inactive episode is NOT SOC-ignorable (it still blocks a
 * new admission) but IS ROC-ignorable (there is no active episode to resume).
 */
export function isIgnorableEpisodeForSoc(ep: EpisodeListItem): boolean {
  return (
    ep.admission_active === false ||
    (hasAdmissionDischargeDate(ep) && ep.active === false)
  );
}

/**
 * ROC view of "ignore this episode". A ROC resumes the patient's *current* episode by
 * adding visits to it, so the only thing that matters is whether the episode itself is
 * confirmed active. Anything that is not explicitly active (`active !== true`) can never
 * be resumed — both an inactive episode (`active === false`) and one whose active state is
 * unknown (`active === null`); you cannot add visits to an inactive episode, and an
 * unknown-active episode must not be offered as a bound-less ROC. This holds even when the
 * admission is still active and undischarged (an open admission sitting on a non-active
 * episode). When every episode is ROC-ignorable there is nothing to resume, so ROC must be
 * blocked.
 *
 * This is the single source of truth for "resumable episode": both `hasActiveEpisodeForRoc`
 * ("is ROC available") and `getLatestActiveEpisode` ("which episode bounds the ROC dates")
 * route through it, so they cannot disagree — i.e. "ROC available" always implies "there is
 * an episode to bound it" (ROG-2819).
 */
export function isIgnorableEpisodeForRoc(ep: EpisodeListItem): boolean {
  return ep.active !== true;
}

/**
 * True when the matched patient has an open admission that prevents creating a new
 * SOC. Kinnser/WellSky will not allow a new admission while a prior one is still
 * open (admission active + no discharge date). SOC-ignorable episodes are skipped.
 */
export function hasOpenAdmissionBlockingSoc(
  episodes: EpisodeListItem[]
): boolean {
  return episodes
    .filter(ep => !isIgnorableEpisodeForSoc(ep))
    .some(ep => ep.admission_active === true && !hasAdmissionDischargeDate(ep));
}

/**
 * True when the matched patient has at least one episode a ROC could resume — i.e. a
 * still-active episode (not ROC-ignorable). When every episode is inactive (or there are
 * none), there is nothing to resume, so ROC must be blocked. This is independent of the
 * SOC open-admission check: an open admission on an inactive episode blocks SOC yet still
 * leaves nothing to resume, so ROC is blocked too.
 */
export function hasActiveEpisodeForRoc(episodes: EpisodeListItem[]): boolean {
  return episodes.some(ep => !isIgnorableEpisodeForRoc(ep));
}

/**
 * Whether "New admission" may be selected for a SOC. Never allowed while an open
 * admission blocks SOC. Otherwise allowed when EITHER the matched (latest active)
 * episode is discharged OR the patient has no active episode at all (no episode left to
 * resume) — in the latter case there is nothing to constrain a fresh admission, so a SOC
 * can be created with any date.
 *
 * The discharge-date check alone is insufficient on both ends: an open admission can
 * sit on a non-latest / inactive episode that `getLatestActiveEpisode` never returns
 * (so the open-admission flag must be consulted independently — ROG-2819), and a patient
 * with no active episode has no matched discharge date yet should still allow a SOC.
 */
export function canSelectNewAdmissionForSoc(
  hasMatchedEpisodeDischargeDate: boolean,
  socBlockedByOpenAdmission: boolean,
  hasNoActiveEpisode: boolean
): boolean {
  if (socBlockedByOpenAdmission) return false;
  return hasMatchedEpisodeDischargeDate || hasNoActiveEpisode;
}

/** Normalize an episode date string to YYYY-MM-DD for API, or undefined if invalid. */
export function toEpisodeDateOnly(
  value: string | null | undefined
): string | undefined {
  if (!value || !value.trim()) return undefined;
  const d = new Date(value.trim());
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
}

/**
 * Episode-derived intake fields persisted whenever a patient is matched (in the match
 * modal) or re-synced (via "Refresh from Kinnser" on the scheduling step). Always fully
 * populated so the backend stays in sync when the match changes.
 *
 * The `kinnser_matched_*` fields describe a Kinnser/WellSky episode, so they are only
 * meaningful for a Kinnser match. A Roger match has no Kinnser episode data, so every
 * field is cleared to null — crucially NOT an explicit `false` for the has-open-admission
 * / has-active-episode flags. The scheduling step reads those flags as a real "Kinnser
 * says..." signal; writing `false` for a Roger match would wrongly block ROC and flip the
 * "New admission" gate (ROG-2819). Clearing to null (rather than omitting) also keeps
 * stale Kinnser fields from lingering when a prior Kinnser match is switched to Roger.
 *
 * Identity fields (patient key, match source, is_new_patient) are set by the caller —
 * this is the episode-derived subset only.
 */
export function buildMatchedEpisodeFields(
  patient: PatientMatchResult
): Partial<IntakeResponse> {
  // Only an explicit Roger match is treated as "no Kinnser episode data". Kinnser-only
  // callers (e.g. "Refresh from Kinnser") may omit match_source; default them to Kinnser.
  const isRogerMatch = patient.match_source === 'roger_emr';
  const episodes = isRogerMatch ? [] : (patient.episodes_info ?? []);
  const latest = getLatestActiveEpisode(episodes);
  const fields: Partial<IntakeResponse> = {
    kinnser_matched_has_open_admission: isRogerMatch
      ? null
      : hasOpenAdmissionBlockingSoc(episodes),
    kinnser_matched_has_active_episode: isRogerMatch
      ? null
      : hasActiveEpisodeForRoc(episodes),
  };
  if (latest) {
    fields.kinnser_matched_discharge_date =
      toEpisodeDateOnly(latest.discharge_date) ?? null;
    fields.kinnser_matched_start_of_care =
      toEpisodeDateOnly(latest.start_of_care) ?? null;
    fields.kinnser_matched_episode_start_date =
      toEpisodeDateOnly(latest.start_date) ?? null;
    fields.kinnser_matched_episode_end_date =
      toEpisodeDateOnly(latest.end_date) ?? null;
    fields.kinnser_matched_episode_key =
      latest.episode_key && latest.episode_key.trim() !== ''
        ? latest.episode_key.trim()
        : null;
    // Pre-populate clinician from the matched episode's assigned clinician.
    fields.kinnser_clinician_id = latest.am_user_key?.trim() || null;
  } else {
    fields.kinnser_matched_discharge_date = null;
    fields.kinnser_matched_start_of_care = null;
    fields.kinnser_matched_episode_start_date = null;
    fields.kinnser_matched_episode_end_date = null;
    fields.kinnser_matched_episode_key = null;
    fields.kinnser_clinician_id = null;
  }
  return fields;
}
